import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { POSITIONS, FEET } from "../lib/constants";
import { toast } from "sonner";
import {
  CirclePlus, UserRoundSearch, X, Trash, SquarePen, FileUser, Award, TrafficCone, Goal,
  Bandage, UserRound, LoaderCircle,
} from "lucide-react";

const EMPTY = {
  name: "", jersey_number: "", position: "", age: "", nationality: "", phone: "",
  preferred_foot: "", height: "", weight: "", status: "fit", past_injuries: "", notes: "",
};

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2.5 rounded-xl bg-[#f7fdfa] border border-[#d1fae5] focus:border-[#2a3b19] outline-none text-sm";

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState(null); // player being created/edited
  const [saving, setSaving] = useState(false);
  const [activity, setActivity] = useState(null);

  const load = async () => {
    const { data } = await api.get("/players");
    setPlayers(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setForm({ ...EMPTY });
  const openEdit = (p) => setForm({ ...EMPTY, ...p, jersey_number: p.jersey_number ?? "" });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      jersey_number: form.jersey_number ? parseInt(form.jersey_number) : null,
      age: form.age ? parseInt(form.age) : null,
      height: form.height ? parseFloat(form.height) : null,
      weight: form.weight ? parseFloat(form.weight) : null,
    };
    try {
      if (form.id) await api.put(`/players/${form.id}`, payload);
      else await api.post("/players", payload);
      toast.success("تم حفظ اللاعب");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف اللاعب؟")) return;
    await api.delete(`/players/${id}`);
    toast.success("تم الحذف");
    load();
  };

  const openActivity = async (p) => {
    setActivity({ loading: true, player: p });
    const { data } = await api.get(`/players/${p.id}/activity`);
    setActivity({ ...data, loading: false });
  };

  const filtered = players.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (q && !p.name.includes(q)) return false;
    return true;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-medium text-[#2a3b19] uppercase tracking-wider">القائمة</p>
          <h1 className="text-3xl font-black">اللاعبون</h1>
        </div>
        <button data-testid="add-player-btn" onClick={openNew} className="px-5 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center gap-2">
          <CirclePlus size={18} /> إضافة لاعب
        </button>
      </div>

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <UserRoundSearch size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="player-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم..." className={`${inputCls} pr-10`} />
        </div>
        <select data-testid="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputCls + " max-w-[160px]"}>
          <option value="all">كل الحالات</option>
          <option value="fit">جاهز</option>
          <option value="injured">مصاب</option>
        </select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <div key={p.id} data-testid={`player-card-${p.id}`} className="glass border border-[#d1fae5] rounded-2xl p-5 hover:border-[#2a3b19]/40 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#2a3b19]/10 flex items-center justify-center font-black text-[#2a3b19] text-lg">
                  {p.jersey_number ?? "•"}
                </div>
                <div>
                  <div className="font-bold">{p.name}</div>
                  <div className="text-xs text-slate-600">{p.position || "—"}</div>
                </div>
              </div>
              <span className={`text-[11px] px-2 py-1 rounded-lg font-medium ${p.status === "injured" ? "bg-red-500/15 text-red-500" : "bg-[#2a3b19]/10 text-[#2a3b19]"}`}>
                {p.status === "injured" ? "مصاب" : "جاهز"}
              </span>
            </div>
            <div className="flex gap-3 text-xs text-slate-600 mt-4">
              {p.age && <span>{p.age} سنة</span>}
              {p.nationality && <span>{p.nationality}</span>}
              {p.preferred_foot && <span>{p.preferred_foot}</span>}
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid={`player-activity-${p.id}`} onClick={() => openActivity(p)} className="flex-1 py-2 rounded-lg bg-[#ecfdf5] hover:bg-[#d1fae5] text-xs font-medium flex items-center justify-center gap-1">
                <FileUser size={14} /> الملف
              </button>
              <button onClick={() => openEdit(p)} className="p-2 rounded-lg bg-[#ecfdf5] hover:bg-[#d1fae5]" data-testid={`edit-player-${p.id}`}><SquarePen size={15} /></button>
              <button onClick={() => remove(p.id)} className="p-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20" data-testid={`delete-player-${p.id}`}><Trash size={15} /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-slate-500 col-span-full text-center py-12">لا يوجد لاعبون.</p>}
      </div>

      {/* form modal */}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setForm(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="glass border border-[#d1fae5] rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="player-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">{form.id ? "تعديل لاعب" : "لاعب جديد"}</h2>
              <button type="button" onClick={() => setForm(null)}><X size={22} className="text-slate-600" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="الاسم"><input required data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
              <Field label="رقم التيشرت"><input type="number" data-testid="pf-number" value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} className={inputCls} /></Field>
              <Field label="المركز">
                <select data-testid="pf-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className={inputCls}>
                  <option value="">اختر</option>
                  {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="العمر"><input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className={inputCls} /></Field>
              <Field label="الجنسية"><input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} className={inputCls} /></Field>
              <Field label="رقم الهاتف"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></Field>
              <Field label="القدم المفضلة">
                <select value={form.preferred_foot} onChange={(e) => setForm({ ...form, preferred_foot: e.target.value })} className={inputCls}>
                  <option value="">اختر</option>
                  {FEET.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="الحالة">
                <select data-testid="pf-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                  <option value="fit">جاهز</option>
                  <option value="injured">مصاب</option>
                </select>
              </Field>
              <Field label="الطول (سم)"><input type="number" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} className={inputCls} /></Field>
              <Field label="الوزن (كغ)"><input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className={inputCls} /></Field>
            </div>
            <div className="mt-3">
              <Field label="الإصابات السابقة"><textarea value={form.past_injuries} onChange={(e) => setForm({ ...form, past_injuries: e.target.value })} className={inputCls} rows={2} /></Field>
            </div>
            <div className="mt-3">
              <Field label="ملاحظات عامة"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} rows={2} /></Field>
            </div>
            <button data-testid="save-player-btn" disabled={saving} className="w-full mt-5 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center justify-center gap-2">
              {saving && <LoaderCircle className="animate-spin" size={18} />} حفظ
            </button>
          </form>
        </div>
      )}

      {/* activity / dossier modal */}
      {activity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setActivity(null)}>
          <div onClick={(e) => e.stopPropagation()} className="glass border border-[#d1fae5] rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="player-dossier">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold flex items-center gap-2"><UserRound className="text-[#2a3b19]" size={22} /> {activity.player?.name}</h2>
              <button onClick={() => setActivity(null)}><X size={22} className="text-slate-600" /></button>
            </div>
            {activity.loading ? (
              <div className="py-12 flex justify-center"><LoaderCircle className="animate-spin text-[#2a3b19]" size={28} /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: "نسبة الحضور", value: activity.attendance_rate != null ? `${activity.attendance_rate}%` : "—", icon: TrafficCone },
                    { label: "مباريات لعبها", value: activity.matches_played ?? 0, icon: Goal },
                    { label: "معدل التدريب", value: activity.avg_training_rating != null ? `${activity.avg_training_rating}%` : "—", icon: Award },
                    { label: "معدل المباريات", value: activity.avg_match_rating != null ? `${activity.avg_match_rating}%` : "—", icon: Award },
                  ].map((s, i) => (
                    <div key={i} className="bg-[#f7fdfa] border border-[#d1fae5] rounded-xl p-3 text-center">
                      <s.icon size={16} className="mx-auto text-[#2a3b19] mb-1" />
                      <div className="text-xl font-black">{s.value}</div>
                      <div className="text-[10px] text-slate-600">{s.label}</div>
                    </div>
                  ))}
                </div>

                {activity.player?.past_injuries && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-4 text-sm flex gap-2">
                    <Bandage size={16} className="text-red-500 shrink-0 mt-0.5" />
                    <div><span className="text-red-500 font-medium">إصابات سابقة:</span> {activity.player.past_injuries}</div>
                  </div>
                )}

                <h3 className="font-bold mb-3 text-slate-200">سجل النشاط (مترابط تلقائياً)</h3>
                <div className="space-y-2">
                  {activity.timeline?.length === 0 && <p className="text-slate-500 text-sm py-4 text-center">لا توجد ملاحظات أو تقييمات بعد. أضفها من الحصص أو المباريات.</p>}
                  {activity.timeline?.map((t, i) => (
                    <div key={i} className="bg-[#f7fdfa] border border-[#d1fae5] rounded-xl p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          {t.type === "training" ? <TrafficCone size={15} className="text-[#2a3b19]" /> : <Goal size={15} className="text-[#2a3b19]" />}
                          {t.title}
                        </div>
                        {t.rating != null && <span className="text-xs font-bold text-[#2a3b19]">{t.rating}%</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{t.date}</div>
                      {t.note && <p className="text-sm text-slate-700 mt-2">{t.note}</p>}
                      {(t.goals > 0 || (t.cards && t.cards.length > 0)) && (
                        <div className="flex gap-3 text-xs mt-2">
                          {t.goals > 0 && <span className="text-[#2a3b19]">⚽ {t.goals} هدف</span>}
                          {t.cards?.map((c, ci) => <span key={ci} className={c === "red" ? "text-red-500" : "text-[#2a3b19]"}>🟥 بطاقة {c === "red" ? "حمراء" : "صفراء"}</span>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
